import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { useCart, SIZES, COLORS, PRODUCT, inr } from "@/context/CartContext";

const FACTS = ["BLACK / OFF WHITE", "OVERSIZED FIT", "LIMITED EDITION — 200 PIECES"];
const VIEWS = { FRONT: "/images/campaign-2.webp", BACK: "/images/back-full.webp" };
const SHOP_VIEWS = { FRONT: "/images/voice-1.webp", BACK: "/images/back-full.webp" };
const OFFWHITE_VIEWS = { FRONT: "/images/offwhite-front.webp", BACK: "/images/offwhite-back.webp" };

function usePurchase() {
  const { add, setDrawerOpen } = useCart();
  const [color, setColor] = useState("BLACK");
  const [size, setSize] = useState(null);
  const [qty, setQtyLocal] = useState(1);
  const [error, setError] = useState(false);
  const [added, setAdded] = useState(false);
  const [stock, setStock] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stock`)
      .then((r) => r.json())
      .then(setStock)
      .catch(() => {});
  }, []);

  const handleAdd = () => {
    if (!size) {
      setError(true);
      document.querySelector('[data-testid="size-selector"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    add(size, qty, color);
    setAdded(true);
    setTimeout(() => setAdded(false), 1400);
    setDrawerOpen(true);
  };

  return { color, setColor, size, setSize, qty, setQtyLocal, error, setError, added, handleAdd, stock };
}

function ProductImage({ src, label, testid, loupe = false, imgClass = "h-[52vh] sm:h-[64vh]", fit = "cover" }) {
  const ref = useRef(null);
  const [lens, setLens] = useState(null);
  const rx = useSpring(useMotionValue(0), { stiffness: 140, damping: 16 });
  const ry = useSpring(useMotionValue(0), { stiffness: 140, damping: 16 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    rx.set(-py * 9);
    ry.set(px * 13);
    if (loupe) setLens({ x: (px + 0.5) * 100, y: (py + 0.5) * 100 });
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
    setLens(null);
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="group relative overflow-hidden bg-black border border-black/10"
      style={{ perspective: 900 }}
      data-testid={testid}
      data-cursor="look"
    >
      <motion.img
        src={src}
        alt={`${label} view of THE SHUTUP T-SHIRT`}
        style={{ rotateX: rx, rotateY: ry }}
        className={`w-full will-change-transform ${fit === "contain" ? "object-contain" : "object-cover object-top"} ${imgClass}`}
      />
      <span className="absolute top-4 left-4 bg-paper text-black font-sans text-[11px] font-bold tracking-[0.3em] px-3 py-1.5">
        {label}
      </span>
      {loupe && lens && (
        <div
          data-testid="product-loupe"
          className="pointer-events-none absolute w-40 h-40 rounded-full border-2 border-pink hidden sm:block"
          style={{
            left: `${lens.x}%`,
            top: `${lens.y}%`,
            transform: "translate(-50%, -50%)",
            backgroundImage: `url(${src})`,
            backgroundSize: "300%",
            backgroundPosition: `${lens.x}% ${lens.y}%`,
            boxShadow: "0 0 0 4px rgba(0,0,0,0.45)",
          }}
        />
      )}
      {loupe && (
        <span className="absolute bottom-4 right-4 bg-pink text-black font-sans text-[10px] font-bold tracking-[0.25em] px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          HOVER TO INSPECT
        </span>
      )}
    </div>
  );
}

function PurchasePanel({ compact = false, p }) {
  const { color, setColor, size, setSize, qty, setQtyLocal, error, setError, added, handleAdd, stock } = p;

  return (
    <div data-testid="purchase-module">
      <p className="font-sans font-bold text-sm tracking-[0.3em] mb-4">SELECT COLOUR</p>
      <div className="grid grid-cols-2 gap-2 max-w-md" data-testid="color-selector">
        {COLORS.map((c) => (
          <button
            key={c}
            data-testid={`color-option-${c.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setColor(c)}
            className={`flex items-center justify-center gap-3 font-sans font-bold text-sm sm:text-base py-4 border-2 border-black transition-colors ${
              color === c ? "bg-black text-paper" : "bg-transparent hover:bg-acid"
            }`}
          >
            <span
              className={`w-5 h-5 rounded-full border ${c === "BLACK" ? "bg-ink border-white/30" : "bg-paper border-black/40"}`}
            />
            {c}
          </button>
        ))}
      </div>
      {color === "OFF WHITE" && (
        <p className="font-hand text-2xl text-black/60 mt-3 -rotate-1" data-testid="off-white-note">
          same chaos, cream canvas.
        </p>
      )}

      <div className={`flex items-center justify-between mb-4 ${compact ? "mt-6" : "mt-10"}`}>
        <p className="font-sans font-bold text-sm tracking-[0.3em]">SELECT SIZE</p>
        {error && !size && (
          <p className="font-sans text-blood text-xs font-bold tracking-[0.2em]" data-testid="size-error">
            ← PICK A SIZE FIRST
          </p>
        )}
      </div>
      <div className={`grid grid-cols-3 sm:grid-cols-6 gap-2 ${error && !size ? "ring-2 ring-blood" : ""}`} data-testid="size-selector">
        {SIZES.map((s) => (
          <button
            key={s}
            data-testid={`size-option-${s.toLowerCase()}`}
            onClick={() => {
              setSize(s);
              setError(false);
            }}
            className={`font-sans font-bold text-base sm:text-lg py-4 sm:py-5 border-2 border-black transition-colors ${
              size === s ? "bg-black text-paper" : "bg-transparent hover:bg-acid"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className={`flex flex-wrap items-center gap-5 ${compact ? "mt-6" : "mt-8"}`}>
        <div className="flex items-center border-2 border-black" data-testid="quantity-selector">
          <button
            data-testid="quantity-decrease"
            onClick={() => setQtyLocal((q) => Math.max(1, q - 1))}
            className="font-sans font-bold text-xl px-5 py-3.5 hover:bg-black hover:text-paper transition-colors"
          >
            −
          </button>
          <span data-testid="quantity-value" className="font-sans font-bold text-lg w-12 text-center">
            {qty}
          </span>
          <button
            data-testid="quantity-increase"
            onClick={() => setQtyLocal((q) => Math.min(10, q + 1))}
            className="font-sans font-bold text-xl px-5 py-3.5 hover:bg-black hover:text-paper transition-colors"
          >
            +
          </button>
        </div>

        <motion.button
          data-testid="add-to-cart-button"
          data-cursor="shutup"
          onClick={handleAdd}
          whileTap={{ scale: 0.97 }}
          className={`flex-1 min-w-[240px] font-sans font-bold tracking-[0.12em] text-base sm:text-lg px-8 py-4 transition-colors ${
            added ? "bg-turq text-black" : "bg-black text-paper hover:bg-pink hover:text-black"
          }`}
        >
          {added ? "ADDED TO CART ✓" : `ADD TO CART — ${inr(PRODUCT.price * qty)}`}
        </motion.button>
      </div>
      <p className="font-sans text-black/50 text-xs tracking-[0.2em] mt-5">
        SHUTUP &amp; SPEAK WILD + LIMITED EDITION POSTER
      </p>

      {stock && (
        <div className="mt-6" data-testid="stock-counter">
          <div className="flex items-end justify-between mb-2">
            <p className="font-sans font-bold text-sm tracking-[0.2em]">
              <span className="text-blood" data-testid="stock-sold">{stock.sold}</span> / {stock.total} CLAIMED
            </p>
            <p className="font-hand text-xl text-black/60 rotate-1">when they're gone, they're gone.</p>
          </div>
          <div className="h-3 border-2 border-black bg-transparent overflow-hidden">
            <motion.div
              data-testid="stock-bar"
              initial={{ width: 0 }}
              whileInView={{ width: `${Math.min(100, (stock.sold / stock.total) * 100)}%` }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="h-full bg-pink"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function Product({ kicker = "02 — THE PRODUCT", compact = false }) {
  const [view, setView] = useState("FRONT");
  const p = usePurchase();

  if (compact) {
    const views = p.color === "OFF WHITE" ? OFFWHITE_VIEWS : SHOP_VIEWS;
    return (
      <section id="product" data-testid="product-section" className="bg-paper text-ink py-8 sm:py-12">
        <div className="mx-auto max-w-7xl px-5 sm:px-10">
          <div className="grid md:grid-cols-2 gap-6 lg:gap-12 md:items-start">
            <div>
              <ProductImage
                src={views[view]}
                label={`${view} — ${p.color}`}
                testid={`product-image-${view.toLowerCase()}`}
                loupe={view === "BACK"}
                imgClass="h-[46vh] sm:h-[56vh] lg:h-[62vh]"
                fit="contain"
              />
              <div className="grid grid-cols-2 gap-2 mt-3">
                {Object.keys(VIEWS).map((v) => (
                  <button
                    key={v}
                    data-testid={`view-${v.toLowerCase()}`}
                    onClick={() => setView(v)}
                    className={`font-sans font-bold text-xs tracking-[0.25em] py-3 border-2 border-black transition-colors ${
                      view === v ? "bg-black text-paper" : "bg-transparent hover:bg-acid"
                    }`}
                  >
                    {v} VIEW
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="font-sans text-xs font-semibold tracking-[0.35em] text-black/50 mb-2">{kicker}</p>
              <div className="flex flex-wrap items-end justify-between gap-2 mb-6">
                <h2 className="font-display text-[clamp(2rem,8vw,4rem)] leading-[0.9]" data-testid="product-title">
                  THE SHUTUP T-SHIRT
                </h2>
                <p className="font-sans font-bold text-xl sm:text-2xl" data-testid="product-price">
                  {inr(PRODUCT.price)}
                </p>
              </div>
              <PurchasePanel compact p={p} />
              <div className="grid grid-cols-2 lg:grid-cols-3 border-2 border-black divide-x-2 divide-y-2 lg:divide-y-0 divide-black mt-8" data-testid="product-facts">
                {FACTS.map((f, i) => (
                  <p key={f} className={`font-sans font-semibold text-[10px] sm:text-xs tracking-[0.18em] px-3 py-4 text-center ${i === FACTS.length - 1 ? "col-span-2 lg:col-span-1" : ""}`}>
                    {f}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div
          data-testid="shop-sticky-bar"
          className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-black/95 backdrop-blur border-t border-white/15 px-4 py-3 flex items-center justify-between gap-3"
        >
          <div>
            <p className="font-sans font-bold text-paper text-xs tracking-[0.15em]" data-testid="shop-sticky-selection">
              {p.size ? `${p.color} / ${p.size}` : "PICK A SIZE ↑"}
            </p>
            <p className="font-sans text-paper/60 text-xs mt-0.5">{inr(PRODUCT.price * p.qty)}</p>
          </div>
          <button
            data-testid="shop-sticky-add"
            onClick={p.handleAdd}
            className={`font-sans font-bold text-xs tracking-[0.15em] px-6 py-3.5 transition-colors ${
              p.added ? "bg-turq text-black" : "bg-pink text-black hover:bg-acid"
            }`}
          >
            {p.added ? "ADDED ✓" : "ADD TO CART →"}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="product" data-testid="product-section" className="bg-paper text-ink py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-10">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-4 border-b-2 border-black pb-6">
            <div>
              <p className="font-sans text-xs font-semibold tracking-[0.35em] text-black/50 mb-4">{kicker}</p>
              <h2 className="font-display text-[clamp(2.6rem,7vw,6rem)] leading-[0.9]" data-testid="product-title">
                THE SHUTUP T-SHIRT
              </h2>
            </div>
            <p className="font-sans font-bold text-2xl sm:text-3xl" data-testid="product-price">
              {inr(PRODUCT.price)}
            </p>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-5 sm:gap-8 mt-10">
          <Reveal>
            <ProductImage
              src={p.color === "OFF WHITE" ? OFFWHITE_VIEWS.FRONT : VIEWS.FRONT}
              label={`FRONT — ${p.color}`}
              testid="product-image-front"
              fit={p.color === "OFF WHITE" ? "contain" : "cover"}
            />
          </Reveal>
          <Reveal delay={0.12}>
            <ProductImage
              src={p.color === "OFF WHITE" ? OFFWHITE_VIEWS.BACK : VIEWS.BACK}
              label={`BACK — ${p.color}`}
              testid="product-image-back"
              loupe
              fit={p.color === "OFF WHITE" ? "contain" : "cover"}
            />
          </Reveal>
        </div>

        <Reveal className="mt-10">
          <div className="grid grid-cols-2 lg:grid-cols-3 border-2 border-black divide-x-2 divide-y-2 lg:divide-y-0 divide-black" data-testid="product-facts">
            {FACTS.map((f, i) => (
              <p key={f} className={`font-sans font-semibold text-xs sm:text-sm tracking-[0.18em] px-4 py-5 text-center ${i === FACTS.length - 1 ? "col-span-2 lg:col-span-1" : ""}`}>
                {f}
              </p>
            ))}
          </div>
        </Reveal>

        <Reveal className="mt-12 max-w-3xl">
          <PurchasePanel p={p} />
        </Reveal>
      </div>
    </section>
  );
}
